// PSX glossary — the canonical, citable definitions behind /glossary.
// Each shortDef is written to stand alone as a quotable answer; body paragraphs
// add mechanics and the toolHref points at the ClearCurve page showing the data.

export type GlossaryFaq = { question: string; answer: string };

export type GlossaryTerm = {
  slug: string;
  term: string;
  fullName?: string;
  category: "Participants & Flow" | "Settlement" | "Leverage & Futures" | "Market Mechanics" | "Indices & Corporate";
  shortDef: string;
  body: string[];
  faq: GlossaryFaq[];
  related: string[];
  toolLabel?: string;
  toolHref?: string;
  keywords: string[];
};

export const glossaryTerms: GlossaryTerm[] = [
  {
    slug: "fipi",
    term: "FIPI",
    fullName: "Foreign Investor Portfolio Investment",
    category: "Participants & Flow",
    shortDef:
      "FIPI (Foreign Investor Portfolio Investment) is the daily record of net buying and selling by foreign investors on the Pakistan Stock Exchange, published by NCCPL. A positive FIPI means foreigners bought more than they sold that day.",
    body: [
      "NCCPL publishes FIPI every trading day, broken down by foreign investor category (foreign corporates, foreign individuals, overseas Pakistanis) and by sector. It is reported in US dollars and Pakistani rupees, as gross buy, gross sell, and net value.",
      "Traders watch FIPI as a proxy for institutional conviction: sustained foreign buying in a sector often precedes re-rating, while heavy foreign selling can cap rallies even when local sentiment is positive. Because it is aggregated, FIPI never names specific stocks — attributing sector flow to individual symbols requires inference from liquidity, delivery, and block-deal data.",
    ],
    faq: [
      {
        question: "Where does FIPI data come from?",
        answer:
          "The National Clearing Company of Pakistan Limited (NCCPL) compiles FIPI from settled trades and publishes it daily after market close on its website.",
      },
      {
        question: "Does positive FIPI mean the market will rise?",
        answer:
          "Not by itself. Foreign flows on the PSX are small relative to local liquidity in many sessions; FIPI is best read as one input alongside local participant flow (LIPI), delivery ratios, and price action.",
      },
    ],
    related: ["lipi", "uin", "delivery-ratio"],
    toolLabel: "Participant Flow (FIPI/LIPI) dashboard",
    toolHref: "/money-flow/flows",
    keywords: ["FIPI", "foreign investor portfolio investment", "PSX foreign buying", "NCCPL FIPI"],
  },
  {
    slug: "lipi",
    term: "LIPI",
    fullName: "Local Investor Portfolio Investment",
    category: "Participants & Flow",
    shortDef:
      "LIPI (Local Investor Portfolio Investment) is the daily record of net buying and selling by local investor categories on the Pakistan Stock Exchange — individuals, mutual funds, banks/DFIs, companies, insurance firms, brokers, and other organizations — published by NCCPL.",
    body: [
      "LIPI is the local counterpart of FIPI. Because every foreign sale must be absorbed by a local buyer (and vice versa), the two reports mirror each other: net FIPI plus net LIPI sums to roughly zero each day.",
      "The category breakdown is what makes LIPI useful: a decline absorbed by mutual funds and insurance companies reads very differently from one absorbed by leveraged individuals. Persistent accumulation by institutional local categories is often treated as smart-money positioning.",
    ],
    faq: [
      {
        question: "What is the difference between FIPI and LIPI?",
        answer:
          "FIPI covers foreign investors; LIPI covers local investor categories. They are two halves of the same daily flow report published by NCCPL, and their net values offset each other.",
      },
      {
        question: "Which LIPI categories are considered institutional?",
        answer:
          "Mutual funds, banks/DFIs, insurance companies, and other organizations are usually read as institutional; individuals and much of broker proprietary trading are read as retail or speculative flow.",
      },
    ],
    related: ["fipi", "delivery-ratio", "uin"],
    toolLabel: "Participant Flow (FIPI/LIPI) dashboard",
    toolHref: "/money-flow/flows",
    keywords: ["LIPI", "local investor portfolio investment", "PSX local flows", "NCCPL LIPI"],
  },
  {
    slug: "uin",
    term: "UIN",
    fullName: "Unique Identification Number",
    category: "Settlement",
    shortDef:
      "A UIN (Unique Identification Number) is the single identity NCCPL assigns to each investor in Pakistan's capital market, linking all of that investor's accounts across every broker. It allows trades to be netted per investor rather than per account.",
    body: [
      "Because the UIN follows the investor across brokers, UIN-level settlement data reveals how much of a day's trading actually changed ownership: if the same investor bought and sold through different brokers, UIN netting cancels it out.",
      "PSX publishes daily UIN settlement reports per symbol — trade volume and value versus UIN-settled volume and value. The ratio between them (the delivery ratio) is one of the cleanest available measures of conviction versus intraday churn on the PSX.",
    ],
    faq: [
      {
        question: "Why does UIN settlement matter for traders?",
        answer:
          "A stock can trade huge volume with almost no UIN settlement — meaning day traders passed it around without anyone keeping shares. High UIN settlement means real positions were taken, which is a stronger foundation for a move.",
      },
    ],
    related: ["delivery-ratio", "cm-settlement", "t2-settlement"],
    toolLabel: "Settlement Confirmation dashboard",
    toolHref: "/money-flow/settlement",
    keywords: ["UIN", "unique identification number", "PSX UIN settlement", "NCCPL UIN"],
  },
  {
    slug: "delivery-ratio",
    term: "Delivery Ratio",
    category: "Settlement",
    shortDef:
      "The delivery ratio on the PSX is the share of a stock's traded value that went to actual delivery (settled per investor UIN) rather than being squared off the same day. A high delivery ratio signals real positioning; a low one signals intraday speculation.",
    body: [
      "The ratio is computed from PSX's daily UIN settlement report: UIN-settled value divided by total traded value. A stock trading 10 million shares with a 70% delivery ratio saw 7 million shares genuinely change owners; the same volume at 15% was mostly day-trading churn.",
      "Delivery is directionless — it confirms that positions were taken, not which way. Traders pair it with price action and participant flow: rising price on high delivery reads as accumulation, while a rally on very low delivery is often labelled a speculative trap.",
    ],
    faq: [
      {
        question: "What is a good delivery ratio on PSX?",
        answer:
          "There is no fixed threshold, but market-wide averages typically sit near 40–55%. Ratios sustained above ~70% suggest strong conviction; below ~25% on heavy volume suggests speculative churn.",
      },
      {
        question: "Is the delivery ratio bullish or bearish?",
        answer:
          "Neither by itself — it measures conviction, not direction. High delivery during a decline can mean institutions are genuinely exiting, just as high delivery during a rise can mean genuine accumulation.",
      },
    ],
    related: ["uin", "cm-settlement", "lipi"],
    toolLabel: "Settlement Confirmation dashboard",
    toolHref: "/money-flow/settlement",
    keywords: ["delivery ratio PSX", "UIN percentage", "PSX settlement ratio", "conviction"],
  },
  {
    slug: "cm-settlement",
    term: "CM Settlement",
    fullName: "Clearing Member Settlement",
    category: "Settlement",
    shortDef:
      "CM settlement is delivery measured at the Clearing Member (broker) level on the PSX. It is always at or below the UIN ratio, because broker-level netting also cancels trades between different clients of the same broker.",
    body: [
      "Comparing the UIN ratio with the CM ratio for the same stock exposes where netting happened. A wide gap means much of the 'delivery' netted inside one brokerage house — different clients of the same broker trading with each other.",
      "That pattern deserves skepticism: delivery that looks real at the investor level but concentrates within a single broker can be one desk positioned on both sides rather than broad market participation.",
    ],
    faq: [
      {
        question: "Why is the CM ratio lower than the UIN ratio?",
        answer:
          "UIN netting only cancels trades by the same investor. CM netting additionally cancels trades between different investors who use the same broker, so less volume survives to broker-level settlement.",
      },
    ],
    related: ["uin", "delivery-ratio", "t2-settlement"],
    toolLabel: "Settlement Confirmation dashboard",
    toolHref: "/money-flow/settlement",
    keywords: ["CM settlement", "clearing member", "broker netting PSX", "cross broker netting"],
  },
  {
    slug: "blank-sale",
    term: "Blank Sale",
    category: "Leverage & Futures",
    shortDef:
      "A blank sale is Pakistan's regulatory term for a short sale — selling shares or futures contracts without owning them, intending to buy back cheaper. PSX publishes a daily blank sale report for the Deliverable Futures market showing short volume per contract against open interest and free float.",
    body: [
      "On the PSX, outright short selling in the ready (cash) market is tightly restricted, so most shorting happens in Deliverable Futures Contracts (DFC). The daily DFC blank sale report lists each contract's blank-sale volume and value, and expresses it as a percentage of open interest and of the company's free float.",
      "Rising blank sale concentration marks stocks the market is betting against — but it also builds fuel for short squeezes: every blank sale must eventually be bought back, and a rally can force that buying all at once.",
    ],
    faq: [
      {
        question: "Is short selling allowed on the Pakistan Stock Exchange?",
        answer:
          "Yes, within rules. Regular short selling is permitted mainly through deliverable futures and, in the ready market, under specific regulations (including uptick-style constraints and eligible-security lists). The blank sale report makes futures shorting publicly visible daily.",
      },
      {
        question: "How can I see which PSX stocks are being shorted?",
        answer:
          "PSX publishes the DFC blank sale report every trading day, showing blank-sale volume per futures contract as a share of open interest and free float.",
      },
    ],
    related: ["dfc", "open-interest", "slb"],
    toolLabel: "Leverage & Short Pressure dashboard",
    toolHref: "/money-flow/funding",
    keywords: ["blank sale", "short selling PSX", "PSX short sell report", "DFC blank sale"],
  },
  {
    slug: "mts",
    term: "MTS",
    fullName: "Margin Trading System",
    category: "Leverage & Futures",
    shortDef:
      "MTS (Margin Trading System) is NCCPL's regulated platform for leveraged buying in the PSX ready market: financiers fund a portion of an investor's share purchase and earn a markup, with the shares held as collateral. Net open MTS positions are disclosed per symbol every day.",
    body: [
      "The daily MTS report shows, for each eligible symbol, the open financed volume and value, new take-ups, releases, and the weighted-average markup rate. Positions must be unwound within a defined period, so MTS exposure is inherently short-term leverage.",
      "High MTS exposure relative to a stock's free float marks crowded leveraged longs. When prices fall, financed positions face margin pressure and forced selling — which is why leverage data is read as a risk gauge as much as a bullish signal.",
    ],
    faq: [
      {
        question: "What is the difference between MTS and MFS on the PSX?",
        answer:
          "MTS is centralized leverage routed through NCCPL where third-party financiers fund trades against markup. MFS (Margin Financing System) is broker-to-client financing — the broker itself extends the leverage. Both are disclosed daily per symbol.",
      },
    ],
    related: ["mfs", "slb", "free-float"],
    toolLabel: "Leverage & Short Pressure dashboard",
    toolHref: "/money-flow/funding",
    keywords: ["MTS", "margin trading system", "PSX leverage", "NCCPL MTS"],
  },
  {
    slug: "mfs",
    term: "MFS",
    fullName: "Margin Financing System",
    category: "Leverage & Futures",
    shortDef:
      "MFS (Margin Financing System) is broker-provided leverage on the PSX: a brokerage finances part of its client's share purchases against the shares as collateral. Net open MFS positions per symbol, and their share of free float, are disclosed daily.",
    body: [
      "Unlike MTS, where financing is centralized through NCCPL, MFS credit comes from the broker's own book, making it the more common leverage channel. The daily report shows open financed value per symbol and the percentage of free float financed.",
      "A rising share of free float financed through MFS means an increasing slice of the tradable stock is held on borrowed money — supportive while prices rise, but a source of forced selling in declines.",
    ],
    faq: [
      {
        question: "How much of a PSX stock's float can be margin financed?",
        answer:
          "Exposure varies by symbol and broker risk limits; the disclosed metric to watch is the percentage of free float financed. Low single digits is routine — sustained readings above roughly 3–5% mark unusually crowded leverage.",
      },
    ],
    related: ["mts", "free-float", "blank-sale"],
    toolLabel: "Leverage & Short Pressure dashboard",
    toolHref: "/money-flow/funding",
    keywords: ["MFS", "margin financing system", "PSX margin financing", "broker leverage"],
  },
  {
    slug: "slb",
    term: "SLB",
    fullName: "Securities Lending & Borrowing",
    category: "Leverage & Futures",
    shortDef:
      "SLB (Securities Lending and Borrowing) is NCCPL's regulated platform where holders of PSX shares lend them for a fee to borrowers — typically short sellers who must deliver shares they have sold. It is the modern, regulated successor to the informal badla financing era.",
    body: [
      "SLB lets long-term holders (funds, insurance companies) earn incremental return on idle holdings, while giving short sellers a compliant way to source deliverable shares. Lending is collateralized and centrally settled through NCCPL.",
      "SLB activity in a symbol is circumstantial evidence of short positioning in the ready market, complementing the futures blank sale report.",
    ],
    faq: [
      {
        question: "What was badla and how does SLB differ?",
        answer:
          "Badla was Pakistan's informal carry-over financing system, banned in the mid-2000s after repeated crises tied to its opacity and leverage. SLB replaces the share-borrowing function with a collateralized, centrally cleared, and disclosed mechanism.",
      },
    ],
    related: ["blank-sale", "mts", "mfs"],
    keywords: ["SLB", "securities lending and borrowing", "badla", "stock borrowing PSX"],
  },
  {
    slug: "free-float",
    term: "Free Float",
    category: "Market Mechanics",
    shortDef:
      "Free float is the portion of a company's shares actually available for public trading — total shares minus sponsor, government, strategic, and other locked-in holdings. PSX uses free float for index weighting and futures position limits.",
    body: [
      "PSX recalculates free float periodically from pattern-of-shareholding filings. A company can be huge by market capitalization yet have a small float, making its price easy to move on modest volume.",
      "Float is the denominator that turns raw positioning numbers into meaningful ones: futures open interest, blank sales, and margin financing are all best read as a percentage of free float, since that measures how much of the truly tradable supply is spoken for.",
    ],
    faq: [
      {
        question: "Why do the KSE-100 weightings use free float instead of market cap?",
        answer:
          "Free-float weighting makes the index reflect investable reality — shares nobody can buy (sponsor or state holdings) don't drive index weight, which reduces distortion from tightly held companies.",
      },
    ],
    related: ["kse-100", "open-interest", "dfc"],
    keywords: ["free float PSX", "free float meaning", "float shares", "index weighting"],
  },
  {
    slug: "dfc",
    term: "DFC",
    fullName: "Deliverable Futures Contract",
    category: "Leverage & Futures",
    shortDef:
      "A Deliverable Futures Contract (DFC) is a single-stock futures contract on the PSX that settles by physical delivery of shares. Contracts run monthly and expire on the last Friday of the contract month.",
    body: [
      "DFCs are the main venue for both leveraged long exposure and short selling on the PSX. Because settlement is by delivery, whoever is short at expiry must produce actual shares — which is why open interest and blank sale data around expiry matter.",
      "PSX publishes daily per-contract open interest, including it as a percentage of the underlying's free float. Very high OI relative to float marks crowded positioning, and the monthly expiry forces those positions to roll or close.",
    ],
    faq: [
      {
        question: "When do PSX futures expire?",
        answer:
          "Each monthly Deliverable Futures Contract expires on the last Friday of its contract month; the new contract month opens for trading in parallel before expiry.",
      },
      {
        question: "What happens to a futures position at expiry?",
        answer:
          "It must either be closed, rolled into the next month's contract, or taken to physical delivery — longs pay for and receive shares, shorts must deliver them.",
      },
    ],
    related: ["open-interest", "rollover-week", "blank-sale"],
    toolLabel: "Futures & OI dashboard",
    toolHref: "/money-flow/insights?tab=derivatives",
    keywords: ["deliverable futures contract", "DFC", "PSX futures", "single stock futures Pakistan"],
  },
  {
    slug: "open-interest",
    term: "Open Interest",
    category: "Leverage & Futures",
    shortDef:
      "Open interest (OI) is the number of futures contracts that remain open — neither closed nor delivered — in a PSX Deliverable Futures Contract. Rising OI means new positions are being created; falling OI means positions are closing.",
    body: [
      "OI is measured in contracts, but the more useful readings are OI volume as a percentage of the stock's free float (how much of tradable supply is controlled through futures) and the day-over-day change in contracts (fresh positioning versus unwinding).",
      "A caution when reading OI in value terms: OI value equals contracts times price, so a price rally alone inflates OI value without any new positioning. Contract counts are the honest measure of build-up.",
    ],
    faq: [
      {
        question: "What does rising open interest with rising price mean?",
        answer:
          "The classic reading is fresh leveraged buying (long build-up). It is more reliable when measured in contracts rather than value, and outside rollover week — near expiry, OI shifts are partly mechanical.",
      },
    ],
    related: ["dfc", "rollover-week", "free-float"],
    toolLabel: "Futures & OI dashboard",
    toolHref: "/money-flow/insights?tab=derivatives",
    keywords: ["open interest PSX", "OI futures", "long build up", "short build up"],
  },
  {
    slug: "rollover-week",
    term: "Rollover Week",
    category: "Leverage & Futures",
    shortDef:
      "Rollover week is the final week of a PSX futures contract month, when traders close positions in the expiring Deliverable Futures Contract and reopen them in the next month. Prices and open interest often move mechanically during this window rather than on fundamentals.",
    body: [
      "Because PSX futures expire on the last Friday of each month, the preceding days concentrate forced activity: leveraged longs must fund delivery, roll at a cost, or sell. That selling pressure often depresses heavily positioned stocks into expiry, with relief once the roll completes.",
      "Open interest readings are least trustworthy in this window — OI collapses in the expiring contract and rebuilds in the next month, so 'long unwinding' or 'fresh build-up' labels derived from day-over-day OI changes can be artifacts of the calendar.",
    ],
    faq: [
      {
        question: "Why do some PSX stocks fall in rollover week?",
        answer:
          "Stocks with heavy futures open interest face forced selling as leveraged longs unwind or pay to roll positions before the last-Friday expiry. The pressure is calendar-driven and often eases after the roll completes.",
      },
    ],
    related: ["dfc", "open-interest", "blank-sale"],
    keywords: ["rollover week PSX", "futures rollover Pakistan", "contract expiry PSX"],
  },
  {
    slug: "ndm",
    term: "Off-Market / NDM",
    fullName: "Negotiated Deal Market",
    category: "Market Mechanics",
    shortDef:
      "The Negotiated Deal Market (NDM) is PSX's channel for off-market trades: large block deals privately negotiated between two parties and then reported through the exchange, without passing through the regular order book.",
    body: [
      "Because the buyer and seller agree terms directly, off-market deals reveal deliberate, sized positioning — a sponsor selling a stake, an institution accumulating a block, or a cross between related parties. Price and volume of each deal are disclosed.",
      "A block deal is hard evidence of large positioning in a name, which is why flow-attribution models weight off-market activity heavily when guessing where institutional money moved.",
    ],
    faq: [
      {
        question: "Do off-market trades affect the quoted market price?",
        answer:
          "Not directly — they execute outside the order book, often at a premium or discount to the screen price. The deal price itself is informative: a block crossing above market suggests a motivated buyer.",
      },
    ],
    related: ["fipi", "lipi", "free-float"],
    toolLabel: "Off-Market Transactions dashboard",
    toolHref: "/money-flow/off-market",
    keywords: ["negotiated deal market", "off market PSX", "block deal Pakistan", "NDM"],
  },
  {
    slug: "circuit-breakers",
    term: "Circuit Breakers",
    category: "Market Mechanics",
    shortDef:
      "PSX circuit breakers cap how far a stock can move in one session — for most stocks, ±10% of the previous close or Rs 1, whichever is higher. Separate index-level halts pause the entire market during extreme moves.",
    body: [
      "The per-stock band means a fast-moving name simply stops trading at the limit (locked limit-up or limit-down) until opposing orders arrive. Multi-day trends therefore unfold as sequences of capped sessions rather than single large gaps.",
      "Market-wide halts trigger on large intraday moves in the benchmark index, pausing trading to let leverage and panic clear. Traders track limit-locked stocks as a gauge of one-sided pressure.",
    ],
    faq: [
      {
        question: "How much can a PSX stock move in one day?",
        answer:
          "Under the standard price limit, ±10% of the previous close or Rs 1, whichever is higher. The Rs 1 alternative matters for very low-priced shares, letting them move more than 10% in percentage terms.",
      },
    ],
    related: ["kse-100", "free-float"],
    keywords: ["PSX circuit breaker", "price limit PSX", "upper lock lower lock", "KSE halt"],
  },
  {
    slug: "kse-100",
    term: "KSE-100",
    category: "Indices & Corporate",
    shortDef:
      "The KSE-100 is the Pakistan Stock Exchange's benchmark index: 100 companies selected by sector representation and free-float market capitalization, weighted by free float. It is the number quoted when people say 'the PSX went up today.'",
    body: [
      "The index takes the largest company from each sector, then fills remaining slots with the biggest free-float capitalizations overall, and is recomposed semi-annually. Free-float weighting keeps state- and sponsor-locked shares from distorting weights.",
      "Heavyweights in banking, energy, and fertilizer dominate index moves, so the KSE-100 can rally on a handful of large names while the broader market lags — breadth measures and the KSE-All-Shares index give the fuller picture.",
    ],
    faq: [
      {
        question: "How are KSE-100 companies selected?",
        answer:
          "One top company per sector by free-float market cap, plus the largest remaining companies regardless of sector, reviewed and rebalanced on a set schedule by PSX.",
      },
    ],
    related: ["kmi-30", "free-float", "circuit-breakers"],
    toolLabel: "KSE-100 index dashboard",
    toolHref: "/indices/KSE-100",
    keywords: ["KSE-100", "KSE 100 index", "PSX benchmark", "Pakistan stock index"],
  },
  {
    slug: "kmi-30",
    term: "KMI-30",
    fullName: "KSE-Meezan Index",
    category: "Indices & Corporate",
    shortDef:
      "The KMI-30 (KSE-Meezan Index) tracks the 30 most liquid Shariah-compliant companies on the Pakistan Stock Exchange, screened under criteria set with Al-Meezan / Meezan Bank's Shariah board and rebalanced semi-annually.",
    body: [
      "Compliance screens cover the business itself (no conventional banking, alcohol, and similar) and financial ratios such as interest-bearing debt to assets and non-compliant income share. Companies drift in and out of compliance as balance sheets change.",
      "The index matters commercially: Shariah-compliant mutual funds and pension allocations restrict themselves to KMI-eligible names, so gaining or losing compliance shifts a real pool of institutional demand.",
    ],
    faq: [
      {
        question: "What makes a PSX stock Shariah-compliant?",
        answer:
          "Its core business must be permissible and its financials must pass ratio screens — limits on interest-based debt, interest-earning assets, and income from non-compliant sources, with any incidental non-compliant income purified via charity.",
      },
    ],
    related: ["kse-100", "free-float"],
    keywords: ["KMI-30", "Shariah compliant stocks Pakistan", "Islamic index PSX", "Meezan index"],
  },
  {
    slug: "t2-settlement",
    term: "T+2 Settlement",
    category: "Settlement",
    shortDef:
      "PSX ready-market trades settle on a T+2 cycle: shares and money change hands two working days after the trade date, cleared centrally through NCCPL with securities held at CDC.",
    body: [
      "The two-day gap is what makes intraday squaring possible — buying and selling the same shares within the cycle nets to no delivery obligation, which is exactly what UIN settlement data measures.",
      "NCCPL acts as central counterparty, netting obligations per clearing member and per UIN, while the Central Depository Company (CDC) moves the actual book-entry shares.",
    ],
    faq: [
      {
        question: "When do I actually own PSX shares I bought today?",
        answer:
          "Legally the trade is yours immediately, but shares arrive in your CDC sub-account on settlement day, two working days later (T+2). Selling before then is allowed and simply nets against your purchase.",
      },
    ],
    related: ["uin", "cm-settlement", "delivery-ratio"],
    keywords: ["T+2 settlement", "PSX settlement cycle", "NCCPL clearing", "CDC Pakistan"],
  },
  {
    slug: "face-value-split",
    term: "Face Value & Share Splits",
    category: "Indices & Corporate",
    shortDef:
      "Face value is a share's nominal (par) value, traditionally Rs 10 on the PSX. A share split — formally a sub-division of face value, e.g. Rs 10 to Rs 2 — multiplies the share count and divides the price, without changing the company's worth.",
    body: [
      "Splits are usually done to improve affordability and liquidity after a large price run. On the effective date the market price divides by the split ratio, so a 5:1 split turns a Rs 300 stock into five Rs 60 shares.",
      "The trap is historical comparison: EPS, dividends per share, and book value per share reported before the split are on the old share count and must be divided by the ratio to compare with post-split figures — otherwise earnings appear to collapse when nothing changed.",
    ],
    faq: [
      {
        question: "Does a share split change what my investment is worth?",
        answer:
          "No — you hold more shares at a proportionally lower price. Value changes only if the improved liquidity and affordability attract new demand afterwards.",
      },
      {
        question: "Why does a stock's EPS history look broken after a split?",
        answer:
          "Pre-split EPS was earned over fewer shares. Divide historical per-share figures by the split ratio (e.g., by 5 for a Rs 10→Rs 2 split) to put the whole history on the current basis.",
      },
    ],
    related: ["book-closure", "kse-100"],
    keywords: ["face value PSX", "share split Pakistan", "sub-division of shares", "par value"],
  },
  {
    slug: "book-closure",
    term: "Book Closure & Ex-Date",
    category: "Indices & Corporate",
    shortDef:
      "Book closure is the period when a PSX company freezes its shareholder register to determine who receives an announced dividend, bonus, or rights entitlement. To qualify, you must own the shares before the ex-date — buying on or after it means buying without the entitlement.",
    body: [
      "Companies announce a book-closure window with corporate results or actions. Because trades settle T+2, the ex-date falls before the closure start: purchases from the ex-date onward settle too late to make the register.",
      "Prices adjust mechanically on the ex-date — roughly by the dividend amount, or by the dilution factor for bonus and rights issues — so the apparent 'drop' on that morning is entitlement leaving the price, not selling pressure.",
    ],
    faq: [
      {
        question: "If I buy a PSX stock one day before the ex-date, do I get the dividend?",
        answer:
          "Yes — a purchase before the ex-date settles in time to be on the register during book closure, so the entitlement is yours even though cash arrives later.",
      },
    ],
    related: ["t2-settlement", "face-value-split"],
    keywords: ["book closure PSX", "ex date dividend Pakistan", "bonus shares", "rights entitlement"],
  },
];

export const glossaryBySlug = new Map(glossaryTerms.map((t) => [t.slug, t]));
export const glossarySlugs = glossaryTerms.map((t) => t.slug);

export function getGlossaryTerm(slug: string): GlossaryTerm | undefined {
  return glossaryBySlug.get(slug.toLowerCase());
}
